import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PlayerAvatar, UserAvatar } from '../components/ui/Avatar';
import { 
  Search, FileText, CheckCircle, XCircle, Clock, Archive, AlertTriangle, 
  User, Download, Eye, DollarSign, CreditCard, Flag, Shield, Sparkles, Tag, Users, Trash2,
  HeartPulse, MapPin, Calendar, Award
} from 'lucide-react';
import { getDivisionLogo } from '../constants/divisionLogos';
import ManualPaymentModal from '../components/ManualPaymentModal';
import AdminLinkGuardianModal from '../components/AdminLinkGuardianModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Parse "YYYY-MM-DD" (or full-ISO with 'T' suffix) as a LOCAL-time calendar
// date — never UTC — to avoid timezone-driven day shifting on display.
// `new Date("YYYY-MM-DD")` is parsed by the JS spec as UTC midnight, which
// renders as the previous calendar day in any timezone west of UTC.
const parseLocalDate = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// Division display names mapping - Maps age groups and division IDs to display names
const DIVISION_DISPLAY = {
  // Flag Football - Little Chiefs (5-7U)
  'flag_little_chiefs': 'Flag Football — Little Chiefs (5–7U)',
  '5-7U': 'Flag Football — Little Chiefs (5–7U)',
  '5U': 'Flag Football — Little Chiefs (5–7U)',
  '6U': 'Flag Football — Little Chiefs (5–7U)',
  'div_5_6u': 'Flag Football — Little Chiefs (5–7U)',
  'div_little_chiefs': 'Flag Football — Little Chiefs (5–7U)',
  
  // Tackle Football - Tiny Chiefs (7-8U)
  'tackle_tiny_chiefs': 'Tackle Football — Tiny Chiefs (7–8U)',
  '7-8U': 'Tackle Football — Tiny Chiefs (7–8U)',
  '7U': 'Tackle Football — Tiny Chiefs (7–8U)',
  '8U': 'Tackle Football — Tiny Chiefs (7–8U)',
  'div_7u': 'Tackle Football — Tiny Chiefs (7–8U)',
  'div_8u': 'Tackle Football — Tiny Chiefs (7–8U)',
  'div_tiny_chiefs': 'Tackle Football — Tiny Chiefs (7–8U)',
  
  // Tackle Football - Mighty Chiefs (9U)
  'tackle_mighty_chiefs': 'Tackle Football — Mighty Chiefs (9U)',
  '9U': 'Tackle Football — Mighty Chiefs (9U)',
  'div_9u': 'Tackle Football — Mighty Chiefs (9U)',
  'div_mighty_chiefs': 'Tackle Football — Mighty Chiefs (9U)',
  
  // Tackle Football - Junior Chiefs (10U)
  'tackle_junior_chiefs': 'Tackle Football — Junior Chiefs (10U)',
  '10U': 'Tackle Football — Junior Chiefs (10U)',
  'div_10u': 'Tackle Football — Junior Chiefs (10U)',
  'div_junior_chiefs': 'Tackle Football — Junior Chiefs (10U)',
  
  // Tackle Football - Varsity Chiefs (11U)
  'tackle_varsity_chiefs': 'Tackle Football — Varsity Chiefs (11U)',
  '11U': 'Tackle Football — Varsity Chiefs (11U)',
  'div_11u': 'Tackle Football — Varsity Chiefs (11U)',
  'div_varsity_chiefs': 'Tackle Football — Varsity Chiefs (11U)',
  
  // Tackle Football - Senior Chiefs (12U)
  'tackle_senior_chiefs': 'Tackle Football — Senior Chiefs (12U)',
  '12U': 'Tackle Football — Senior Chiefs (12U)',
  'div_12u': 'Tackle Football — Senior Chiefs (12U)',
  'div_senior_chiefs': 'Tackle Football — Senior Chiefs (12U)',
  
  // Tackle Football - Elite Chiefs (13U)
  'tackle_elite_chiefs': 'Tackle Football — Elite Chiefs (13U)',
  '13U': 'Tackle Football — Elite Chiefs (13U)',
  'div_13u': 'Tackle Football — Elite Chiefs (13U)',
  'div_elite_chiefs': 'Tackle Football — Elite Chiefs (13U)',
  
  // Cheer - The Lady Chiefs: Sparks (5-8)
  'cheer_sparks': 'Cheer — The Lady Chiefs: Cheer Sparks (5–8)',
  'div_cheer_sparks': 'Cheer — The Lady Chiefs: Cheer Sparks (5–8)',
  
  // Cheer - The Lady Chiefs: Stars (9-11)
  'cheer_stars': 'Cheer — The Lady Chiefs: Cheer Stars (9–11)',
  'div_cheer_stars': 'Cheer — The Lady Chiefs: Cheer Stars (9–11)',
  
  // Cheer - The Lady Chiefs: Elite (12-14)
  'cheer_elite': 'Cheer — The Lady Chiefs: Cheer Elite (12–14)',
  'div_cheer_elite': 'Cheer — The Lady Chiefs: Cheer Elite (12–14)'
};

// Get division display name from registration data
const getDivisionDisplay = (reg) => {
  // Try division_name first (new format)
  if (reg?.division_name && DIVISION_DISPLAY[reg.division_name]) {
    return DIVISION_DISPLAY[reg.division_name];
  }
  // Try division_id
  if (reg?.division_id && DIVISION_DISPLAY[reg.division_id]) {
    return DIVISION_DISPLAY[reg.division_id];
  }
  // Try player age_group
  if (reg?.player?.age_group && DIVISION_DISPLAY[reg.player.age_group]) {
    return DIVISION_DISPLAY[reg.player.age_group];
  }
  // Fallback to player age_group or N/A
  return reg?.player?.age_group || 'Program not selected';
};

// Get division logo for a registration
const getRegDivisionLogo = (reg) => {
  if (reg?.division_id) {
    return getDivisionLogo(reg.division_id);
  }
  if (reg?.player?.age_group) {
    return getDivisionLogo(reg.player.age_group);
  }
  return null;
};

// Program pricing info
const PROGRAM_INFO = {
  flag_football: { 
    name: 'Flag Football — Little Chiefs', 
    icon: Flag, 
    color: 'bg-blue-100 text-blue-800', 
    standardPrice: 200,
    earlyBirdPrice: 150,
    siblingPrice: 150
  },
  tackle_football: { 
    name: 'Tackle Football', 
    icon: Shield, 
    color: 'bg-navy/10 text-navy', 
    standardPrice: 300,
    earlyBirdPrice: 225,
    siblingPrice: 225
  },
  cheer: { 
    name: 'Cheer — The Lady Chiefs', 
    icon: Sparkles, 
    color: 'bg-red/10 text-red', 
    standardPrice: 400,
    earlyBirdPrice: 325,
    siblingPrice: 325
  }
};

const AdminRegistrationsList = () => {
  const { user } = useAuth();
  const isFinanceRole = user?.role === 'super_admin' || user?.role === 'treasurer';
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedReg, setSelectedReg] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [parentNote, setParentNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [quickReviewPlayer, setQuickReviewPlayer] = useState(null);
  const [quickReviewData, setQuickReviewData] = useState(null);
  const [loadingQuickReview, setLoadingQuickReview] = useState(false);
  const [paymentNote, setPaymentNote] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  // Aug 2026: admin guardian linking modal — reused across Registration
  // Review + Quick Review. { householdId, playerId, playerName } | null.
  const [linkGuardianCtx, setLinkGuardianCtx] = useState(null);
  // Live program-config overlay so admin views reflect admin-edited pricing.
  // Falls back to PROGRAM_INFO defaults if the API is unreachable.
  const [programConfigs, setProgramConfigs] = useState(null);
  // Manual payment modal + ledger state (finance-role only).
  const [mpOpen, setMpOpen] = useState(false);
  const [ledger, setLedger] = useState({ loaded: false, rows: [], summary: null, priceAdjustments: [] });
  const [reversingId, setReversingId] = useState(null);

  useEffect(() => {
    fetchRegistrations();
  }, [showArchived]);

  useEffect(() => {
    // Fetch program configs once on mount so price overlays are available.
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/program-configs`, { withCredentials: true });
        if (Array.isArray(r.data) && r.data.length) setProgramConfigs(r.data);
      } catch (e) {
        // Graceful fallback — stay with PROGRAM_INFO defaults.
      }
    })();
  }, []);

  useEffect(() => {
    filterRegistrations();
  }, [searchTerm, statusFilter, paymentFilter, registrations]);

  const viewDocument = async (documentId) => {
    // Sync-open placeholder window first to preserve user-gesture chain (popup-blocker safe)
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${API_URL}/api/documents/${documentId}/download`, { withCredentials: true });
      const url = res.data?.download_url;
      if (url && win) {
        win.location = url;
      } else {
        if (win) win.close();
        alert('Failed to open document');
      }
    } catch (err) {
      if (win) win.close();
      alert(err.response?.data?.detail || 'Failed to open document');
    }
  };

  const viewWaiverPdf = async (waiverType) => {
    // Sync-open placeholder window first to preserve user-gesture chain (popup-blocker safe)
    const win = window.open('', '_blank');
    try {
      const reg = selectedReg || {};
      const waiver = (reg.waivers || []).find(w => w.waiver_type === waiverType);
      const isSigned = waiver?.status === 'signed';

      const res = await axios.get(`${API_URL}/api/waivers/pdf/${waiverType}`, { withCredentials: true });
      const url = res.data?.pdf_url;
      if (!url || !win) {
        if (win) win.close();
        alert('Failed to open waiver PDF');
        return;
      }

      // Unsigned: behavior identical to today - open the blank template directly
      if (!isSigned) {
        win.location = url;
        return;
      }

      // Signed: render a "Signed Waiver Record" page with header strip + embedded blank template iframe
      const labelMap = {
        participation: 'Participation & Liability',
        medical_consent: 'Emergency Medical Treatment Consent',
        code_of_conduct: 'Code of Conduct'
      };
      const fieldLabelMap = {
        participant_name: 'Participant Name',
        participant_dob: 'Participant Date of Birth',
        parent_guardian_name: 'Parent/Legal Guardian Full Name',
        relationship_to_participant: 'Relationship to Participant',
        contact_phone: 'Phone / Email',
        media_permission: 'Media Permission',
        emergency_contact_name: 'Emergency Contact Name',
        emergency_contact_phone: 'Emergency Contact Phone',
        medical_conditions: 'Medical Conditions / Allergies / Medications',
        // Legacy keys (older signed waivers) for graceful display
        medical_info: 'Allergies / Medical Conditions / Medications',
        participant_full_name: 'Participant Full Name'
      };
      const expectedFieldsByWaiver = {
        participation: ['participant_name', 'participant_dob', 'parent_guardian_name', 'relationship_to_participant', 'contact_phone', 'media_permission'],
        medical_consent: ['participant_name', 'participant_dob', 'emergency_contact_name', 'emergency_contact_phone', 'medical_conditions'],
        code_of_conduct: ['participant_name', 'participant_dob', 'parent_guardian_name', 'contact_phone']
      };
      const mediaPermissionLabels = { yes: 'Granted', no: 'Not granted at this time' };
      const playerName = `${reg.player?.first_name || ''} ${reg.player?.last_name || ''}`.trim() || 'Unknown player';
      const waiverLabel = labelMap[waiverType] || waiverType;
      const signedBy = waiver.signature || 'N/A';
      const signedAt = waiver.signed_at ? new Date(waiver.signed_at).toLocaleString() : 'N/A';
      const esc = (s) => String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

      const expectedKeys = expectedFieldsByWaiver[waiverType] || [];
      const fv = waiver.field_values || {};
      const submittedRows = expectedKeys.map(k => {
        let raw = fv[k];
        // Friendly display for media_permission radio value
        if (k === 'media_permission' && raw && mediaPermissionLabels[raw]) raw = mediaPermissionLabels[raw];
        const val = (raw === undefined || raw === null || String(raw).trim() === '') ? '—' : String(raw);
        const label = fieldLabelMap[k] || k;
        return `<tr><th>${esc(label)}</th><td>${esc(val)}</td></tr>`;
      }).join('');
      const fieldsPanel = expectedKeys.length === 0 ? '' : `
  <section class="panel" data-testid="admin-waiver-fields-panel-${esc(waiverType)}">
    <h2>Submitted Information <span class="primary-tag">Primary Record</span></h2>
    <table><tbody>${submittedRows}</tbody></table>
  </section>`;

      const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Signed Waiver Record - ${esc(playerName)} - ${esc(waiverLabel)}</title>
<style>
  *{box-sizing:border-box}html,body{margin:0;min-height:100%;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;background:#f8fafc;color:#0f172a}
  header{background:#0f1c3f;color:#fff;padding:14px 20px;border-bottom:3px solid #d4af37}
  header .title{font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:#d4af37;font-weight:700}
  header .name{font-size:20px;font-weight:700;margin-top:2px}
  header .meta{display:flex;flex-wrap:wrap;gap:8px 24px;margin-top:10px;font-size:13px}
  header .meta .lbl{color:#cbd5e1;margin-right:6px}
  header .badge{display:inline-block;background:#16a34a;color:#fff;padding:2px 10px;border-radius:999px;font-size:12px;font-weight:600;margin-left:8px}
  .panel{background:#fff;padding:20px}
  .panel h2{font-size:13px;letter-spacing:.06em;text-transform:uppercase;color:#475569;margin:0 0 12px;font-weight:700;display:flex;align-items:center;gap:8px}
  .primary-tag{display:inline-block;background:#0f1c3f;color:#d4af37;padding:1px 8px;border-radius:999px;font-size:10px;letter-spacing:.06em;text-transform:uppercase}
  .panel table{width:100%;border-collapse:collapse;font-size:14px;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden}
  .panel th{text-align:left;width:38%;color:#475569;font-weight:600;padding:10px 14px;background:#f8fafc;border-bottom:1px solid #e2e8f0;vertical-align:top;white-space:normal}
  .panel td{padding:10px 14px;border-bottom:1px solid #e2e8f0;color:#0f172a;white-space:pre-wrap;word-break:break-word}
  .panel tr:last-child th,.panel tr:last-child td{border-bottom:0}
  .ref{background:#f8fafc;border-top:1px solid #e2e8f0;padding:0 20px 20px}
  .ref details{background:#fff;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden}
  .ref summary{padding:12px 16px;cursor:pointer;font-size:13px;font-weight:600;color:#475569;background:#f1f5f9;list-style:none;display:flex;align-items:center;gap:8px}
  .ref summary::-webkit-details-marker{display:none}
  .ref summary::before{content:'▸';transition:transform .2s;color:#94a3b8}
  .ref details[open] summary::before{transform:rotate(90deg)}
  .ref summary .reflbl{color:#0f1c3f;letter-spacing:.04em;text-transform:uppercase;font-size:11px;font-weight:700}
  .ref summary .refnote{color:#64748b;font-weight:400;margin-left:auto}
  .ref iframe{display:block;width:100%;height:70vh;min-height:400px;border:0;background:#fff;border-top:1px solid #e2e8f0}
</style></head>
<body>
  <header>
    <div class="title">Signed Waiver Record<span class="badge">Electronically Agreed</span></div>
    <div class="name">${esc(playerName)} &middot; ${esc(waiverLabel)}</div>
    <div class="meta">
      <div><span class="lbl">Signed by:</span><strong>${esc(signedBy)}</strong></div>
      <div><span class="lbl">Signed on:</span><strong>${esc(signedAt)}</strong></div>
    </div>
  </header>${fieldsPanel}
  <div class="ref" data-testid="admin-waiver-reference-section">
    <details>
      <summary><span class="reflbl">Reference Document</span><span class="refnote">Blank template — for reference only</span></summary>
      <iframe src="${esc(url)}" title="Reference waiver template"></iframe>
    </details>
  </div>
</body></html>`;

      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (err) {
      if (win) win.close();
      alert(err.response?.data?.detail || 'Failed to open waiver PDF');
    }
  };

  const fetchRegistrations = async () => {
    try {
      const url = showArchived 
        ? `${API_URL}/api/admin/registrations/list?archived=true`
        : `${API_URL}/api/admin/registrations/list`;
      const response = await axios.get(url, { withCredentials: true });
      setRegistrations(response.data);
      setFilteredRegistrations(response.data);
      // Keep the open detail drawer in sync (e.g., after a standalone note save).
      setSelectedReg((prev) =>
        prev ? response.data.find((r) => r.registration_id === prev.registration_id) || prev : prev
      );
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRegistrations = () => {
    let filtered = registrations;

    if (searchTerm) {
      filtered = filtered.filter(reg =>
        reg.player?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reg.player?.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(reg => reg.status === statusFilter);
    }

    if (paymentFilter !== 'all') {
      filtered = filtered.filter(reg => reg.payment_status === paymentFilter);
    }

    setFilteredRegistrations(filtered);
  };

  const updateRegistrationStatus = async (registrationId, newStatus) => {
    setUpdating(true);
    setError('');
    try {
      const payload = { status: newStatus };
      if (adminNote.trim()) payload.admin_note = adminNote;
      if (parentNote.trim()) payload.parent_note = parentNote;
      
      await axios.put(
        `${API_URL}/api/admin/registrations/${registrationId}/status`,
        payload,
        { withCredentials: true }
      );
      await fetchRegistrations();
      setSelectedReg(null);
      setAdminNote('');
      setParentNote('');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Standalone save for Admin Notes (Internal) — no status change.
  // Pushes a new note onto the registration's append-only admin_notes log.
  const saveAdminNoteOnly = async () => {
    if (!selectedReg || !adminNote.trim() || updating) return;
    setUpdating(true);
    setError('');
    try {
      await axios.put(
        `${API_URL}/api/admin/registrations/${selectedReg.registration_id}/status`,
        { admin_note: adminNote.trim() },
        { withCredentials: true }
      );
      await fetchRegistrations();
      // Keep the drawer open and refresh the selected reg with the new entry.
      setSelectedReg((prev) => prev ? { ...prev } : prev);
      setAdminNote('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save admin note');
    } finally {
      setUpdating(false);
    }
  };

  // Standalone save for Missing Items / Parent Note — overwrite single field.
  const saveParentNoteOnly = async () => {
    if (!selectedReg || updating) return;
    setUpdating(true);
    setError('');
    try {
      await axios.put(
        `${API_URL}/api/admin/registrations/${selectedReg.registration_id}/status`,
        { parent_note: parentNote },
        { withCredentials: true }
      );
      await fetchRegistrations();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save parent note');
    } finally {
      setUpdating(false);
    }
  };

  const confirmPayment = async (registrationId) => {
    setUpdating(true);
    setError('');
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/registrations/${registrationId}/payment/confirm`,
        { payment_note: paymentNote },
        { withCredentials: true }
      );
      await fetchRegistrations();
      setPaymentNote('');
      
      // Show success message with receipt info
      const receiptInfo = response.data.receipt_email_sent 
        ? `Receipt email sent. Receipt #: ${response.data.receipt_number}`
        : 'Payment confirmed successfully.';
      alert(receiptInfo);
      
      // Refresh selected reg data
      if (selectedReg && selectedReg.registration_id === registrationId) {
        const updated = registrations.find(r => r.registration_id === registrationId);
        if (updated) setSelectedReg({ ...updated, payment_status: 'confirmed' });
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setUpdating(false);
    }
  };

  const waivePayment = async (registrationId) => {
    const reason = prompt('Enter reason for waiving payment (e.g., scholarship, staff child):');
    if (reason === null) return;
    
    setUpdating(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/admin/registrations/${registrationId}/payment/waive`,
        { reason },
        { withCredentials: true }
      );
      await fetchRegistrations();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to waive payment');
    } finally {
      setUpdating(false);
    }
  };

  const rejectPayment = async (registrationId) => {
    const reason = prompt('Enter reason for rejecting payment (e.g., payment not received, wrong amount):');
    if (reason === null) return;
    
    setUpdating(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/admin/registrations/${registrationId}/payment/reject`,
        { reason },
        { withCredentials: true }
      );
      await fetchRegistrations();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setUpdating(false);
    }
  };

  const archiveRegistration = async (registrationId) => {
    setUpdating(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/registrations/${registrationId}/archive`,
        {},
        { withCredentials: true }
      );
      await fetchRegistrations();
      setSelectedReg(null);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to archive');
    } finally {
      setUpdating(false);
    }
  };

  // Manual-payment ledger: fetch + reverse. Finance-role only; the UI
  // that triggers these is already gated behind `isFinanceRole`.
  const fetchLedger = async (registrationId) => {
    if (!registrationId) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/registrations/${registrationId}/payments`,
        { withCredentials: true }
      );
      setLedger({
        loaded: true,
        rows: res.data?.payments || [],
        summary: res.data?.summary || null,
        priceAdjustments: res.data?.price_adjustments || [],
      });
    } catch (err) {
      setLedger({ loaded: true, rows: [], summary: null, priceAdjustments: [] });
    }
  };

  const reversePayment = async (paymentId) => {
    if (!selectedReg) return;
    const reason = window.prompt('Reason for reversing this payment (required):');
    if (!reason || !reason.trim()) return;
    setReversingId(paymentId);
    try {
      await axios.post(
        `${API_URL}/api/admin/registrations/${selectedReg.registration_id}/payments/${paymentId}/reverse`,
        { reason: reason.trim() },
        { withCredentials: true }
      );
      await fetchLedger(selectedReg.registration_id);
      await fetchRegistrations();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reverse payment');
    } finally {
      setReversingId(null);
    }
  };

  // Load ledger whenever a finance-role user opens the review modal.
  useEffect(() => {
    if (selectedReg && isFinanceRole) {
      fetchLedger(selectedReg.registration_id);
    } else {
      setLedger({ loaded: false, rows: [], summary: null, priceAdjustments: [] });
    }
  }, [selectedReg?.registration_id, isFinanceRole]);


  const unarchiveRegistration = async (registrationId) => {
    setUpdating(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/registrations/${registrationId}/unarchive`,
        {},
        { withCredentials: true }
      );
      await fetchRegistrations();
      setSelectedReg(null);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to unarchive');
    } finally {
      setUpdating(false);
    }
  };

  const openDeleteConfirm = (player, registration) => {
    setPlayerToDelete({ ...player, registration_id: registration?.registration_id });
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };

  const handleDeletePlayer = async () => {
    if (!playerToDelete) return;
    
    const expectedText = `DELETE ${playerToDelete.first_name}`.toUpperCase();
    if (deleteConfirmText.toUpperCase() !== expectedText) {
      alert(`Please type "DELETE ${playerToDelete.first_name}" to confirm.`);
      return;
    }
    
    setUpdating(true);
    try {
      await axios.delete(
        `${API_URL}/api/players/${playerToDelete.player_id}`,
        { withCredentials: true }
      );
      setShowDeleteConfirm(false);
      setPlayerToDelete(null);
      setDeleteConfirmText('');
      setSelectedReg(null);
      await fetchRegistrations();
      alert(`${playerToDelete.first_name} ${playerToDelete.last_name} has been permanently deleted.`);
    } catch (err) {
      console.error('Failed to delete player:', err);
      alert(err.response?.data?.detail || 'Failed to delete player. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const openQuickReview = async (playerId) => {
    setLoadingQuickReview(true);
    setQuickReviewPlayer(playerId);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/players/${playerId}/quick-review`,
        { withCredentials: true }
      );
      setQuickReviewData(response.data);
    } catch (error) {
      console.error('Failed to load quick review:', error);
    } finally {
      setLoadingQuickReview(false);
    }
  };

  // Open Review modal seeded from list row, then asynchronously enrich with quick-review data
  // (parent.user.picture, secondary_parents, division_name, program_type, etc.) so Review is the
  // complete admin record without weakening any existing actions.
  const openReviewModal = async (reg) => {
    setSelectedReg(reg);
    setParentNote(reg.parent_note || '');
    setPaymentNote('');
    const playerId = reg?.player?.player_id;
    if (!playerId) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/players/${playerId}/quick-review`, { withCredentials: true });
      const qr = res.data || {};
      setSelectedReg(prev => {
        if (!prev || prev.registration_id !== reg.registration_id) return prev;
        return {
          ...prev,
          // Enrich parent with embedded user (incl. picture) when available
          parent: qr.parent || prev.parent,
          household: qr.household || prev.household,
          secondary_parents: qr.secondary_parents || [],
          division_id: qr.division_id ?? prev.division_id,
          division_name: qr.division_name ?? prev.division_name,
          program_type_resolved: qr.program_type ?? null,
          team: qr.team ?? prev.team
        };
      });
    } catch (err) {
      console.error('Failed to enrich review data:', err);
    }
  };

  const exportRoster = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/roster/export`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roster_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export roster:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'not_started': { label: 'Not Started', className: 'bg-gray-100 text-gray-800' },
      'in_progress': { label: 'In Progress', className: 'bg-blue-100 text-blue-800' },
      'submitted': { label: 'Submitted', className: 'bg-purple-100 text-purple-800' },
      'pending_review': { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800' },
      'incomplete': { label: 'Incomplete', className: 'bg-orange-100 text-orange-800' },
      'approved': { label: 'Approved', className: 'bg-green-100 text-green-800' },
      'denied': { label: 'Denied', className: 'bg-red-100 text-red-800' },
      'waitlisted': { label: 'Waitlisted', className: 'bg-indigo-100 text-indigo-800' },
      'archived': { label: 'Archived', className: 'bg-gray-200 text-gray-600' }
    };
    return statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
  };

  const getPaymentBadge = (paymentStatus) => {
    const map = {
      'not_started': { label: 'Not Started', className: 'bg-gray-100 text-gray-800' },
      'pending': { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      'submitted': { label: 'Submitted', className: 'bg-blue-100 text-blue-800' },
      'confirmed': { label: 'Confirmed', className: 'bg-green-100 text-green-800' },
      'failed': { label: 'Rejected', className: 'bg-red-100 text-red-800' },
      'waived': { label: 'Waived', className: 'bg-purple-100 text-purple-800' },
      'paid': { label: 'Paid', className: 'bg-green-100 text-green-800' },
      'partial': { label: 'Partial', className: 'bg-yellow-100 text-yellow-800' },
      'overpaid': { label: 'Overpaid', className: 'bg-amber-100 text-amber-900' },
      'refunded': { label: 'Refunded', className: 'bg-orange-100 text-orange-800' }
    };
    return map[paymentStatus] || { label: paymentStatus || 'Unknown', className: 'bg-gray-100 text-gray-800' };
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'cash': 'Cash',
      'cash_app': 'Cash App',
      'zelle': 'Zelle',
      'venmo': 'Venmo',
      'check': 'Check',
      'external_card': 'External Card',
      'other': 'Other',
      'clover_card': 'Credit/Debit Card',
      'shopify_payment_plan': 'Pay Later / Payment Plan',
      'waived': 'Waived'
    };
    return labels[method] || method || 'Not selected';
  };

  // Resolve a program's display info — admin-edited DB values overlay
  // the static PROGRAM_INFO visual defaults (icon, color).
  const getProgramInfo = (programType) => {
    const defaults = PROGRAM_INFO[programType];
    if (!defaults) return null;
    const live = (programConfigs || []).find(c => c.program_key === programType);
    if (!live) return defaults;
    return {
      ...defaults,
      name: live.name || defaults.name,
      standardPrice: live.standard_price ?? defaults.standardPrice,
      earlyBirdPrice: live.early_bird_price ?? defaults.earlyBirdPrice,
      siblingPrice: live.sibling_price ?? defaults.siblingPrice,
    };
  };

  const getProgramBadge = (programType) => {
    const program = getProgramInfo(programType);
    if (!program) return null;
    const Icon = program.icon;
    return (
      <Badge className={`${program.color} flex items-center space-x-1`}>
        <Icon className="w-3 h-3" />
        <span>{program.name}</span>
      </Badge>
    );
  };

  const getExpectedAmount = (reg) => {
    // Use total_amount from registration if set (includes discount calculations)
    if (reg.total_amount) {
      return reg.total_amount;
    }
    // Fallback to program standard price (live config if available)
    const programType = reg.registration_data?.program_type?.program_type || reg.program_type;
    const program = getProgramInfo(programType);
    if (program) {
      return program.standardPrice;
    }
    return 0;
  };

  const getDiscountInfo = (reg) => {
    const discountType = reg.discount_type;
    const standardAmount = reg.standard_amount || 0;
    const finalAmount = reg.total_amount || 0;
    const savings = standardAmount - finalAmount;
    
    return {
      discountType,
      standardAmount,
      finalAmount,
      savings,
      earlyBirdApplied: discountType === 'early_bird',
      siblingDiscountApplied: discountType === 'sibling'
    };
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-registrations-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-navy mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Registration Management
            </h1>
            <p className="text-gray-600">Review and manage player registrations</p>
          </div>
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            <Button
              variant={showArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchived(!showArchived)}
              className={showArchived ? "bg-navy" : ""}
            >
              <Archive className="w-4 h-4 mr-2" />
              {showArchived ? "Viewing Archived" : "View Archived"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportRoster}
              data-testid="export-roster-btn"
              title="Exports the currently filtered list — click a Collections Snapshot chip first to export only those rows."
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search by player name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Registration Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="pending_review">Pending Review</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="denied">Denied</SelectItem>
                  <SelectItem value="waitlisted">Waitlisted</SelectItem>
                </SelectContent>
              </Select>
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger data-testid="payment-filter">
                  <SelectValue placeholder="Payment Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payments</SelectItem>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="submitted">Submitted (Awaiting)</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="failed">Rejected</SelectItem>
                  <SelectItem value="waived">Waived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Finance F2 — Outstanding Balances summary. Read-only roll-up
            of the same `registrations` list already in state. No new API
            call, no schema change. Clicking a chip applies the matching
            payment filter so admins can drill straight into that group. */}
        {(() => {
          const groups = {
            awaiting: [],
            partial: [],
            paid: [],
            waived: [],
            rejected: [],
            unpaid: [],
          };
          registrations.forEach((reg) => {
            const status = reg.payment_status;
            const paid = Number(reg.amount_paid || 0);
            const total = Number(reg.total_amount || 0);
            if (status === 'waived') {
              groups.waived.push(reg);
            } else if (status === 'failed' || status === 'rejected') {
              groups.rejected.push(reg);
            } else if (status === 'confirmed' || status === 'paid') {
              groups.paid.push(reg);
            } else if (status === 'submitted' || status === 'pending') {
              groups.awaiting.push(reg);
            } else if (paid > 0 && paid < total) {
              groups.partial.push(reg);
            } else {
              groups.unpaid.push(reg);
            }
          });
          const sumOutstanding = (rows) =>
            rows.reduce(
              (acc, r) =>
                acc +
                Math.max(
                  0,
                  Number(r.total_amount || 0) - Number(r.amount_paid || 0)
                ),
              0
            );
          const totalOutstanding =
            sumOutstanding(groups.awaiting) +
            sumOutstanding(groups.partial) +
            sumOutstanding(groups.unpaid);
          const totalCollected = registrations.reduce(
            (acc, r) => acc + Number(r.amount_paid || 0),
            0
          );
          const chips = [
            { key: 'awaiting', label: 'Awaiting Confirmation', filter: 'submitted',
              rows: groups.awaiting, color: 'bg-amber-50 border-amber-200 text-amber-900' },
            { key: 'partial', label: 'Partial', filter: 'pending',
              rows: groups.partial, color: 'bg-orange-50 border-orange-200 text-orange-900' },
            { key: 'unpaid', label: 'Unpaid', filter: 'not_started',
              rows: groups.unpaid, color: 'bg-rose-50 border-rose-200 text-rose-900' },
            { key: 'paid', label: 'Paid / Confirmed', filter: 'confirmed',
              rows: groups.paid, color: 'bg-green-50 border-green-200 text-green-900' },
            { key: 'waived', label: 'Waived', filter: 'waived',
              rows: groups.waived, color: 'bg-sky-50 border-sky-200 text-sky-900' },
            { key: 'rejected', label: 'Rejected', filter: 'failed',
              rows: groups.rejected, color: 'bg-gray-100 border-gray-300 text-gray-700' },
          ];
          return (
            <Card className="mb-8" data-testid="collections-summary-card">
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <CardTitle className="text-navy text-lg">
                    Collections Snapshot
                  </CardTitle>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                    {isFinanceRole && (
                      <>
                        <span data-testid="collections-total-outstanding">
                          Outstanding:{' '}
                          <span className="font-semibold text-navy">
                            ${totalOutstanding.toFixed(2)}
                          </span>
                        </span>
                        <span data-testid="collections-total-collected">
                          Collected (to date):{' '}
                          <span className="font-semibold text-navy">
                            ${totalCollected.toFixed(2)}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {chips.map((c) => {
                    const outstanding =
                      ['awaiting', 'partial', 'unpaid'].includes(c.key)
                        ? sumOutstanding(c.rows)
                        : null;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setPaymentFilter(c.filter)}
                        className={`text-left rounded-lg border px-3 py-2 transition hover:shadow-sm ${c.color} ${
                          paymentFilter === c.filter ? 'ring-2 ring-navy/40' : ''
                        }`}
                        data-testid={`collections-chip-${c.key}`}
                      >
                        <div className="text-xs font-medium uppercase tracking-wide opacity-80">
                          {c.label}
                        </div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="text-2xl font-bold">
                            {c.rows.length}
                          </span>
                          {outstanding != null && c.rows.length > 0 && isFinanceRole && (
                            <span className="text-xs opacity-80">
                              ${outstanding.toFixed(2)} due
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500 mt-3">
                  Click any group to filter the list below. Outstanding total
                  combines Awaiting + Partial + Unpaid.
                </p>
              </CardContent>
            </Card>
          );
        })()}

        {/* Registrations List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-navy">
              {showArchived ? "Archived " : ""}Registrations ({filteredRegistrations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredRegistrations.length > 0 ? (
              <div className="space-y-4">
                {filteredRegistrations.map((reg) => {
                  const statusBadge = getStatusBadge(reg.status);
                  const paymentBadge = getPaymentBadge(reg.payment_status);
                  const programType = reg.registration_data?.program_type?.program_type;
                  const expectedAmount = getExpectedAmount(reg);
                  
                  return (
                    <div key={reg.registration_id} className="border rounded-lg p-4 hover:bg-gray-50" data-testid={`registration-row-${reg.registration_id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3 flex-1">
                          {/* Player Avatar */}
                          <PlayerAvatar
                            photoUrl={reg.player?.photo_url}
                            firstName={reg.player?.first_name}
                            lastName={reg.player?.last_name}
                            size="md"
                          />
                          <div className="flex-1 min-w-0">
                            <button 
                              className="font-medium text-navy hover:underline text-left"
                              onClick={() => openQuickReview(reg.player?.player_id)}
                              data-testid={`player-link-${reg.registration_id}`}
                            >
                              {reg.player?.first_name} {reg.player?.last_name}
                            </button>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              {getRegDivisionLogo(reg) && (
                                <img 
                                  src={getRegDivisionLogo(reg)} 
                                  alt="" 
                                  className="w-6 h-6 object-contain"
                                />
                              )}
                              <span>
                                {getDivisionDisplay(reg)}
                                {reg.player?.grade ? ` · Grade ${reg.player.grade}` : ''}
                              </span>
                            </div>
                            
                            {/* Program Type and Payment badges */}
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {programType && getProgramBadge(programType)}
                              <Badge className={paymentBadge.className}>
                                <DollarSign className="w-3 h-3 mr-1" />
                                {paymentBadge.label}
                              </Badge>
                              {isFinanceRole && (
                                <span className="text-xs text-gray-500" data-testid={`reg-amount-${reg.registration_id}`}>
                                  ${reg.amount_paid?.toFixed(2) || '0.00'} / ${expectedAmount.toFixed(2)}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                Docs: {reg.document_count || 0} | Waivers: {reg.waiver_count || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Badge className={statusBadge.className}>
                            {statusBadge.label}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReviewModal(reg)}
                            data-testid={`review-btn-${reg.registration_id}`}
                          >
                            Review
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-600">No registrations found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Review Modal */}
      {selectedReg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReg(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-6">
              <h2 className="text-2xl font-bold text-navy">
                Review: {selectedReg.player?.first_name} {selectedReg.player?.last_name}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Identity card */}
              <div className="bg-navy/5 rounded-lg p-4" data-testid="admin-review-identity">
                <div className="flex items-start gap-4 flex-wrap">
                  <PlayerAvatar
                    photoUrl={selectedReg.player?.photo_url}
                    firstName={selectedReg.player?.first_name}
                    lastName={selectedReg.player?.last_name}
                    size="xl"
                  />
                  <div className="flex-1 min-w-[200px]">
                    <h3 className="font-bold text-navy text-lg">
                      {(selectedReg.player?.first_name || '—')} {(selectedReg.player?.last_name || '')}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">Player ID: {selectedReg.player?.player_id || '—'}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-3 text-sm">
                      <div><span className="text-gray-500">DOB:</span> {parseLocalDate(selectedReg.player?.date_of_birth)?.toLocaleDateString() ?? '—'}</div>
                      <div><span className="text-gray-500">Age:</span> {(() => { const d = selectedReg.player?.date_of_birth; if (!d) return '—'; const dt=parseLocalDate(d); if (!dt||Number.isNaN(dt.getTime())) return '—'; const now=new Date(); let a=now.getFullYear()-dt.getFullYear(); const m=now.getMonth()-dt.getMonth(); if (m<0||(m===0&&now.getDate()<dt.getDate())) a--; return (a>=0&&a<100)?`${a} yrs`:'—'; })()}</div>
                      <div><span className="text-gray-500">Grade:</span> {selectedReg.player?.grade || '—'}</div>
                      <div><span className="text-gray-500">School:</span> {selectedReg.player?.school || '—'}</div>
                      <div><span className="text-gray-500">Position:</span> {selectedReg.player?.position || '—'}</div>
                      <div><span className="text-gray-500">Jersey:</span> {selectedReg.player?.jersey_number ? `#${selectedReg.player.jersey_number}${selectedReg.player.jersey_size ? ` (${selectedReg.player.jersey_size})` : ''}` : '—'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Program & Team */}
              <div className="border rounded-lg p-4" data-testid="admin-review-program-team">
                <h3 className="font-semibold text-navy mb-3 flex items-center"><Award className="w-4 h-4 mr-2" />Program &amp; Team</h3>
                {(() => {
                  const programResolved = selectedReg.program_type_resolved
                    || selectedReg.registration_data?.program_type?.program_type;
                  const programLabel = PROGRAM_INFO[programResolved]?.name || programResolved || null;
                  const divName = selectedReg.team?.division_name || selectedReg.division_name
                    || (selectedReg.division_id ? DIVISION_DISPLAY[selectedReg.division_id] : null);
                  const teamName = selectedReg.team?.name;
                  const eligibility = selectedReg.player?.eligibility_status;
                  const eligClass = eligibility === 'eligible' ? 'bg-green-100 text-green-800'
                    : (eligibility === 'pending' || eligibility === 'pending_verification') ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800';
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div><span className="text-gray-500">Program:</span> {programLabel || '—'}</div>
                      <div><span className="text-gray-500">Division:</span> {divName || '—'}</div>
                      <div><span className="text-gray-500">Team:</span> {teamName || '—'}</div>
                      <div className="flex items-center gap-2"><span className="text-gray-500">Roster:</span>
                        <Badge className={teamName ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>{teamName ? 'Assigned' : 'Not assigned'}</Badge>
                      </div>
                      <div className="flex items-center gap-2"><span className="text-gray-500">Eligibility:</span>
                        <Badge className={eligClass}>{eligibility || 'Unknown'}</Badge>
                      </div>
                      <div className="flex items-center gap-2"><span className="text-gray-500">Status:</span>
                        <Badge className={getStatusBadge(selectedReg.status).className}>{getStatusBadge(selectedReg.status).label}</Badge>
                      </div>
                      <div className="flex items-center gap-2"><span className="text-gray-500">Payment:</span>
                        <Badge className={getPaymentBadge(selectedReg.payment_status).className}>{getPaymentBadge(selectedReg.payment_status).label}</Badge>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Parent / Guardian + Address */}
              <div className="border rounded-lg p-4" data-testid="admin-review-parents">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-navy flex items-center"><Users className="w-4 h-4 mr-2" />Parent / Guardian</h3>
                  {selectedReg.household?.household_id && (
                    <button
                      onClick={() => setLinkGuardianCtx({
                        householdId: selectedReg.household.household_id,
                        playerId: selectedReg.player?.player_id,
                        playerName: `${selectedReg.player?.first_name || ''} ${selectedReg.player?.last_name || ''}`.trim(),
                      })}
                      className="text-xs font-semibold text-navy hover:underline"
                      data-testid="admin-review-add-guardian-btn"
                    >
                      + {selectedReg.parent ? 'Add Parent / Guardian' : 'Link Parent / Guardian'}
                    </button>
                  )}
                </div>
                {selectedReg.parent ? (
                  <div className="flex items-start gap-3">
                    <UserAvatar photoUrl={selectedReg.parent?.user?.picture} name={selectedReg.parent?.user?.name} size="sm" />
                    <div className="text-sm min-w-0 flex-1">
                      {selectedReg.parent?.user?.email ? (
                        <Link
                          to={`/admin/users?search=${encodeURIComponent(selectedReg.parent.user.email)}`}
                          className="font-semibold text-navy hover:underline"
                          data-testid="admin-review-parent-link"
                        >
                          {selectedReg.parent?.user?.name || '—'}
                        </Link>
                      ) : (
                        <span className="font-semibold">{selectedReg.parent?.user?.name || '—'}</span>
                      )}
                      <div className="text-gray-600">
                        {selectedReg.parent?.user?.email ? (
                          <a className="hover:underline" href={`mailto:${selectedReg.parent.user.email}`}>{selectedReg.parent.user.email}</a>
                        ) : '—'}
                      </div>
                      <div className="text-gray-600">
                        {selectedReg.parent?.user?.phone ? (
                          <a className="hover:underline" href={`tel:${selectedReg.parent.user.phone}`}>{selectedReg.parent.user.phone}</a>
                        ) : '—'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500" data-testid="admin-review-no-parent">No parent/guardian linked</p>
                )}
                {Array.isArray(selectedReg.secondary_parents) && selectedReg.secondary_parents.length > 0 && (
                  <div className="mt-4 pt-3 border-t" data-testid="admin-review-secondary-parents">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Additional Guardian{selectedReg.secondary_parents.length > 1 ? 's' : ''}</p>
                    <div className="space-y-3">
                      {selectedReg.secondary_parents.map((sp, idx) => (
                        <div key={sp.parent_id || idx} className="flex items-start gap-3">
                          <UserAvatar photoUrl={sp.user?.picture} name={sp.user?.name} size="sm" />
                          <div className="text-sm min-w-0">
                            {sp.user?.email ? (
                              <Link to={`/admin/users?search=${encodeURIComponent(sp.user.email)}`} className="font-semibold text-navy hover:underline">
                                {sp.user?.name || '—'}
                              </Link>
                            ) : (
                              <span className="font-semibold">{sp.user?.name || '—'}</span>
                            )}
                            <div className="text-gray-600">{sp.user?.email ? <a className="hover:underline" href={`mailto:${sp.user.email}`}>{sp.user.email}</a> : '—'}</div>
                            <div className="text-gray-600">{sp.user?.phone ? <a className="hover:underline" href={`tel:${sp.user.phone}`}>{sp.user.phone}</a> : '—'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Address line under parent block */}
                {(() => {
                  const h = selectedReg.household || {};
                  const a = h.address;
                  let parts;
                  if (a && typeof a === 'object') parts = [a.street, a.city || h.city, a.state || h.state, a.zip || h.zip];
                  else parts = [a, h.city, h.state, h.zip];
                  const line = parts.filter(Boolean).join(', ');
                  if (!line) return null;
                  return (
                    <div className="mt-3 pt-3 border-t flex items-start text-sm text-gray-700" data-testid="admin-review-address">
                      <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-gray-500" />
                      <span>{line}</span>
                    </div>
                  );
                })()}
              </div>

              {/* Medical Notes / Allergies */}
              <div className={`border rounded-lg p-4 ${selectedReg.player?.medical_notes ? 'bg-amber-50 border-amber-200' : ''}`} data-testid="admin-review-medical">
                <h3 className="font-semibold text-navy mb-2 flex items-center"><HeartPulse className="w-4 h-4 mr-2 text-amber-700" />Medical Notes / Allergies</h3>
                {selectedReg.player?.medical_notes ? (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedReg.player.medical_notes}</p>
                ) : (
                  <p className="text-sm text-gray-500">None on file</p>
                )}
              </div>

              {/* Status and Program Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-navy mb-2">Registration Status</h3>
                  <Badge className={getStatusBadge(selectedReg.status).className}>
                    {getStatusBadge(selectedReg.status).label}
                  </Badge>
                </div>
                <div>
                  <h3 className="font-semibold text-navy mb-2">Program</h3>
                  {selectedReg.registration_data?.program_type?.program_type 
                    ? getProgramBadge(selectedReg.registration_data.program_type.program_type)
                    : <span className="text-gray-500">Not selected</span>
                  }
                </div>
              </div>

              {/* Registration Steps Checklist */}
              {(() => {
                const reg = selectedReg;
                const player = reg.player || {};
                const regData = reg.registration_data || {};
                const waivers = reg.waivers || [];
                const documents = reg.documents || [];

                // Step 1: Player Profile - basic required fields on player record
                const profileFields = [player.first_name, player.last_name, player.date_of_birth, player.grade];
                const profileComplete = profileFields.every(Boolean);

                // Step 2: Required Waivers (3 total, all must be signed)
                const waiverDefs = [
                  { type: 'participation', label: 'Participation & Liability' },
                  { type: 'medical_consent', label: 'Emergency Medical Treatment Consent' },
                  { type: 'code_of_conduct', label: 'Code of Conduct' }
                ];
                const waiverRows = waiverDefs.map(d => {
                  const w = waivers.find(x => x.waiver_type === d.type);
                  return { ...d, signed: w?.status === 'signed', signed_at: w?.signed_at, signature: w?.signature };
                });
                const waiversSignedCount = waiverRows.filter(w => w.signed).length;
                const waiversComplete = waiversSignedCount === waiverDefs.length;

                // Step 3: Uploaded Documents (3 expected types)
                const docDefs = [
                  { type: 'birth_certificate', label: 'Birth Certificate' },
                  { type: 'report_card', label: 'Report Card' },
                  { type: 'medical_consent_form', label: 'Medical Consent Form' }
                ];
                const docRows = docDefs.map(d => {
                  const doc = documents.find(x => x.document_type === d.type);
                  return { ...d, uploaded: !!doc, document_id: doc?.document_id, filename: doc?.filename, uploaded_at: doc?.uploaded_at };
                });
                const docsUploadedCount = docRows.filter(x => x.uploaded).length;
                const docsComplete = docsUploadedCount === docDefs.length;

                // Step 4: Payment status
                const paymentDone = ['confirmed', 'paid', 'waived'].includes(reg.payment_status);
                const paymentBadge = getPaymentBadge(reg.payment_status);

                const StepHeader = ({ done, total, label, count }) => (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {done ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                      )}
                      <span className="font-medium text-navy text-sm">{label}</span>
                    </div>
                    <Badge className={done ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                      {total != null ? `${count}/${total} ${done ? 'complete' : 'pending'}` : (done ? 'Complete' : 'Incomplete')}
                    </Badge>
                  </div>
                );

                return (
                  <div className="border-t pt-4" data-testid="admin-registration-steps">
                    <h3 className="font-semibold text-navy mb-3 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Registration Steps
                    </h3>
                    <div className="space-y-3">
                      {/* Profile */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <StepHeader done={profileComplete} label="Player Profile" />
                        {!profileComplete && (
                          <p className="text-xs text-gray-600 mt-2">
                            Missing: {profileFields.map((v, i) => v ? null : ['first name', 'last name', 'date of birth', 'grade'][i]).filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>

                      {/* Waivers */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <StepHeader done={waiversComplete} total={waiverDefs.length} count={waiversSignedCount} label="Required Waivers / Forms" />
                        <ul className="mt-2 space-y-1">
                          {waiverRows.map(w => (
                            <li key={w.type} className="flex items-center justify-between text-sm" data-testid={`admin-waiver-row-${w.type}`}>
                              <div className="flex items-center gap-2 text-gray-700">
                                {w.signed ? (
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5 text-red-500" />
                                )}
                                <span>{w.label}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-xs text-gray-500">
                                  {w.signed ? (
                                    <span>Signed{w.signature ? ` · ${w.signature}` : ''}{w.signed_at ? ` · ${new Date(w.signed_at).toLocaleDateString()}` : ''}</span>
                                  ) : (
                                    <span className="text-red-600">Not signed</span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="text-xs text-navy hover:underline flex items-center gap-1"
                                  onClick={() => viewWaiverPdf(w.type)}
                                  data-testid={`admin-waiver-view-${w.type}`}
                                >
                                  <Eye className="w-3.5 h-3.5" /> View PDF
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Documents */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <StepHeader done={docsComplete} total={docDefs.length} count={docsUploadedCount} label="Uploaded Documents" />
                        <ul className="mt-2 space-y-1">
                          {docRows.map(d => (
                            <li key={d.type} className="flex items-center justify-between text-sm" data-testid={`admin-doc-row-${d.type}`}>
                              <div className="flex items-center gap-2 text-gray-700">
                                {d.uploaded ? (
                                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5 text-red-500" />
                                )}
                                <span>{d.label}</span>
                              </div>
                              {d.uploaded ? (
                                <button
                                  className="text-xs text-navy hover:underline flex items-center gap-1"
                                  onClick={() => viewDocument(d.document_id)}
                                  data-testid={`admin-doc-view-${d.type}`}
                                >
                                  <Eye className="w-3.5 h-3.5" /> View
                                </button>
                              ) : (
                                <span className="text-xs text-red-600">Missing</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Payment */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {paymentDone ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-orange-500" />
                            )}
                            <span className="font-medium text-navy text-sm">Payment</span>
                          </div>
                          <Badge className={paymentBadge.className}>{paymentBadge.label}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Payment Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-navy mb-3 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Payment Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status:</span>
                    <Badge className={getPaymentBadge(selectedReg.payment_status).className}>
                      {getPaymentBadge(selectedReg.payment_status).label}
                    </Badge>
                  </div>
                  {!isFinanceRole && (
                    <p className="text-xs text-gray-500" data-testid="finance-restricted-note">
                      Financial amounts and payment actions are restricted to Treasurer / Super Admin.
                    </p>
                  )}
                  {isFinanceRole && (
                  <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Method:</span>
                    <span className="font-medium">{getPaymentMethodLabel(selectedReg.payment_method)}</span>
                  </div>
                  
                  {/* Pricing Breakdown */}
                  {selectedReg.standard_amount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Standard Price:</span>
                      <span className={selectedReg.discount_type ? 'line-through text-gray-400' : 'font-medium'}>
                        ${selectedReg.standard_amount?.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedReg.discount_type === 'early_bird' && (
                    <div className="flex justify-between items-center text-green-600">
                      <span className="flex items-center">
                        <Tag className="w-3 h-3 mr-1" />
                        Early Bird Discount:
                      </span>
                      <span>-${(selectedReg.standard_amount - selectedReg.total_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {selectedReg.discount_type === 'sibling' && (
                    <div className="flex justify-between items-center text-purple-600">
                      <span className="flex items-center">
                        <Users className="w-3 h-3 mr-1" />
                        Sibling Discount:
                      </span>
                      <span>-${(selectedReg.standard_amount - selectedReg.total_amount).toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-gray-600 font-medium">Amount Due:</span>
                    <span className="font-bold text-navy">${getExpectedAmount(selectedReg).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Amount Paid:</span>
                    <span className={selectedReg.amount_paid >= getExpectedAmount(selectedReg) ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                      ${selectedReg.amount_paid?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  {selectedReg.payment_submitted_at && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Submitted:</span>
                      <span>{new Date(selectedReg.payment_submitted_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedReg.payment_confirmed_at && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Confirmed:</span>
                      <span>{new Date(selectedReg.payment_confirmed_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedReg.receipt_number && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Receipt #:</span>
                      <span className="font-mono text-navy">{selectedReg.receipt_number}</span>
                    </div>
                  )}
                  {selectedReg.receipt_email_sent && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Receipt Email:</span>
                      <Badge className="bg-green-100 text-green-800">Sent</Badge>
                    </div>
                  )}
                  </>
                  )}
                </div>

                {/* Payment Actions */}
                {isFinanceRole && selectedReg.payment_status === 'submitted' && (
                  <div className="mt-4 p-4 border border-blue-200 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Payment Awaiting Confirmation</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Parent submitted payment via {getPaymentMethodLabel(selectedReg.payment_method)}. 
                      Please verify payment was received before confirming.
                    </p>
                    <div className="mb-3">
                      <label className="text-sm text-gray-600 block mb-1">Payment Note (optional)</label>
                      <Input
                        value={paymentNote}
                        onChange={(e) => setPaymentNote(e.target.value)}
                        placeholder="e.g., Confirmed via Cash App on 1/15"
                        data-testid="payment-note-input"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => confirmPayment(selectedReg.registration_id)}
                        disabled={updating}
                        data-testid="confirm-payment-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Confirm Payment
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => rejectPayment(selectedReg.registration_id)}
                        disabled={updating}
                        data-testid="reject-payment-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                )}

                {/* Payment not yet submitted */}
                {isFinanceRole && ['not_started', 'pending', 'failed'].includes(selectedReg.payment_status) && (
                  <div className="mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => waivePayment(selectedReg.registration_id)}
                      disabled={updating}
                      data-testid="waive-payment-btn"
                    >
                      Waive Payment
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">For scholarships, staff children, etc.</p>
                  </div>
                )}
              </div>

              {/* Manual Payment Ledger + Record Payment (finance-only) */}
              {isFinanceRole && (
                <div className="border-t pt-4" data-testid="payment-ledger-section">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-semibold text-navy flex items-center">
                      <DollarSign className="w-5 h-5 mr-2" />
                      Payment Ledger
                    </h3>
                    <Button
                      size="sm"
                      className="bg-navy hover:bg-navy/90 text-white"
                      onClick={() => setMpOpen(true)}
                      disabled={selectedReg.payment_status === 'waived'}
                      data-testid="record-payment-btn"
                    >
                      + Record Payment
                    </Button>
                  </div>
                  {selectedReg.payment_status === 'waived' && (
                    <p className="text-xs text-gray-500 mb-2">This registration is waived — manual payments are disabled.</p>
                  )}
                  {ledger.summary && (
                    <>
                      {ledger.summary.standard_amount && ledger.summary.standard_amount !== ledger.summary.total_amount && (
                        <div className="text-xs text-gray-600 mb-2" data-testid="ledger-price-line">
                          Standard price <span className="line-through text-gray-400">${Number(ledger.summary.standard_amount).toFixed(2)}</span>
                          <span className="mx-1">·</span>
                          Current final price <span className="font-semibold text-navy">${Number(ledger.summary.final_price || ledger.summary.total_amount).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                        <div className="bg-gray-50 rounded p-2">
                          <div className="text-gray-500 text-xs">Total</div>
                          <div className="font-bold text-navy" data-testid="ledger-total">
                            ${Number(ledger.summary.total_amount || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-2">
                          <div className="text-gray-500 text-xs">Paid</div>
                          <div className="font-bold text-green-600" data-testid="ledger-paid">
                            ${Number(ledger.summary.amount_paid || 0).toFixed(2)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-2">
                          <div className="text-gray-500 text-xs">Remaining</div>
                          <div className="font-bold text-red-600" data-testid="ledger-remaining">
                            ${Number(ledger.summary.remaining_balance || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {ledger.priceAdjustments && ledger.priceAdjustments.length > 0 && (
                    <details className="mb-3 text-xs" data-testid="price-adjustments-history">
                      <summary className="cursor-pointer text-navy font-semibold">
                        Price adjustments ({ledger.priceAdjustments.length})
                      </summary>
                      <table className="min-w-full mt-2">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-right">From</th>
                            <th className="px-2 py-1 text-right">To</th>
                            <th className="px-2 py-1 text-left">Reason</th>
                            <th className="px-2 py-1 text-left">By</th>
                            <th className="px-2 py-1 text-left">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ledger.priceAdjustments.map((a) => (
                            <tr key={a.adjustment_id} className="border-t">
                              <td className="px-2 py-1">{(a.created_at || '').slice(0, 10)}</td>
                              <td className="px-2 py-1 text-right font-mono">${Number(a.previous_final_price).toFixed(2)}</td>
                              <td className="px-2 py-1 text-right font-mono">${Number(a.new_final_price).toFixed(2)}</td>
                              <td className="px-2 py-1 capitalize">{(a.reason || '').replace(/_/g, ' ')}</td>
                              <td className="px-2 py-1">{a.created_by_name || a.created_by || '—'}</td>
                              <td className="px-2 py-1 text-gray-500">{a.internal_note || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
                  {ledger.rows && ledger.rows.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs" data-testid="ledger-table">
                        <thead>
                          <tr className="bg-gray-50 text-gray-600">
                            <th className="px-2 py-1.5 text-left">Date</th>
                            <th className="px-2 py-1.5 text-left">Method</th>
                            <th className="px-2 py-1.5 text-right">Amount</th>
                            <th className="px-2 py-1.5 text-left">Source</th>
                            <th className="px-2 py-1.5 text-left">Entered by</th>
                            <th className="px-2 py-1.5 text-left">Reference</th>
                            <th className="px-2 py-1.5 text-left">Receipt</th>
                            <th className="px-2 py-1.5 text-left">Status</th>
                            <th className="px-2 py-1.5"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {ledger.rows.map((r) => {
                            const cents = Number(r.amount || 0);
                            const displayAmount = (cents / 100).toFixed(2);
                            const isReversal = r.payer_source === 'manual_reversal';
                            const isReversedOriginal = r.status === 'reversed';
                            const canReverse =
                              r.status === 'succeeded' &&
                              (r.payer_source === 'manual' || r.payer_source === 'admin_verify');
                            return (
                              <tr
                                key={r.payment_id}
                                className={`border-t ${isReversal ? 'bg-orange-50/40' : ''}`}
                                data-testid={`ledger-row-${r.payment_id}`}
                              >
                                <td className="px-2 py-1.5">{(r.payment_date || r.created_at || '').slice(0, 10)}</td>
                                <td className="px-2 py-1.5">{getPaymentMethodLabel(r.payment_method) || '—'}</td>
                                <td className={`px-2 py-1.5 text-right font-mono ${isReversal ? 'text-orange-700' : ''}`}>
                                  {isReversal ? '-' : ''}${Math.abs(Number(displayAmount)).toFixed(2)}
                                </td>
                                <td className="px-2 py-1.5 capitalize">{(r.payer_source || '').replace('_', ' ')}</td>
                                <td className="px-2 py-1.5">{r.recorded_by_name || r.recorded_by || '—'}</td>
                                <td className="px-2 py-1.5">
                                  {r.reference_number || r.check_number || (isReversal ? `↩ ${r.reverses_payment_id}` : '—')}
                                </td>
                                <td className="px-2 py-1.5">{r.receipt_number || '—'}</td>
                                <td className="px-2 py-1.5">
                                  {isReversal ? (
                                    <Badge className="bg-orange-100 text-orange-800">Reversal</Badge>
                                  ) : isReversedOriginal ? (
                                    <Badge className="bg-gray-100 text-gray-600">Reversed</Badge>
                                  ) : (
                                    <Badge className="bg-green-100 text-green-800">Succeeded</Badge>
                                  )}
                                </td>
                                <td className="px-2 py-1.5">
                                  {canReverse && (
                                    <button
                                      className="text-xs text-red-600 hover:underline disabled:opacity-40"
                                      onClick={() => reversePayment(r.payment_id)}
                                      disabled={reversingId === r.payment_id}
                                      data-testid={`reverse-btn-${r.payment_id}`}
                                    >
                                      {reversingId === r.payment_id ? 'Reversing…' : 'Reverse'}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500" data-testid="ledger-empty">
                      No payments recorded yet.
                    </p>
                  )}
                </div>
              )}


              {/* Admin Notes Section */}
              <div className="border-t pt-4" data-testid="admin-notes-section">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <h3 className="font-semibold text-navy">Admin Notes (Internal)</h3>
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-800 text-white text-[10px] font-semibold uppercase tracking-wide"
                    data-testid="admin-note-internal-pill"
                  >
                    <Shield className="w-3 h-3" /> Internal only — not visible to parent
                  </span>
                </div>
                <Textarea
                  placeholder="Add internal notes about this registration (e.g., follow-ups, verification questions, board decisions)..."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={2}
                  data-testid="admin-note-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={saveAdminNoteOnly}
                    disabled={!adminNote.trim() || updating}
                    className="bg-slate-800 hover:bg-slate-900 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                    data-testid="admin-note-save-btn"
                  >
                    {updating ? 'Saving…' : 'Save Admin Note'}
                  </Button>
                </div>
                {selectedReg.admin_notes?.length > 0 && (
                  <div
                    className="mt-3 space-y-2 max-h-64 overflow-y-auto"
                    data-testid="admin-note-history"
                  >
                    {[...selectedReg.admin_notes].reverse().map((note, idx) => (
                      <div
                        key={idx}
                        className="text-sm bg-slate-50 border border-slate-200 p-2 rounded"
                        data-testid={`admin-note-entry-${idx}`}
                      >
                        <p className="text-gray-800 whitespace-pre-wrap">{note.note}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-semibold">{note.added_by_name || 'Admin'}</span>
                          {note.added_at ? ` · ${new Date(note.added_at).toLocaleString()}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Parent-Facing Notes Section */}
              <div className="border-t pt-4" data-testid="parent-note-section">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <h3 className="font-semibold text-navy">
                    <AlertTriangle className="w-4 h-4 inline mr-1 text-orange-500" />
                    Missing Items / Parent Note
                  </h3>
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-semibold uppercase tracking-wide"
                    data-testid="parent-note-visible-pill"
                  >
                    <Eye className="w-3 h-3" /> Visible to parent on Family page
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  This message will be visible to the parent on their registration view. Overwrites the current parent-facing note.
                </p>
                <Textarea
                  placeholder="e.g., Birth certificate still missing. Payment has not been completed."
                  value={parentNote}
                  onChange={(e) => setParentNote(e.target.value)}
                  rows={3}
                  data-testid="parent-note-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[11px] text-gray-500">
                    {selectedReg.parent_note_updated_at
                      ? `Last saved ${new Date(selectedReg.parent_note_updated_at).toLocaleString()}`
                      : 'Not yet saved'}
                  </p>
                  <Button
                    size="sm"
                    onClick={saveParentNoteOnly}
                    disabled={updating}
                    className="bg-orange-600 hover:bg-orange-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                    data-testid="parent-note-save-btn"
                  >
                    {updating ? 'Saving…' : 'Save Parent Note'}
                  </Button>
                </div>
              </div>

              {/* Status Update Buttons */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-navy mb-3">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => updateRegistrationStatus(selectedReg.registration_id, 'approved')}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={updating || !['confirmed', 'waived', 'paid'].includes(selectedReg.payment_status)}
                    data-testid="approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  {!['confirmed', 'waived', 'paid'].includes(selectedReg.payment_status) && (
                    <span className="text-xs text-red-600 self-center">Payment must be confirmed first</span>
                  )}
                  <Button
                    size="sm"
                    onClick={() => updateRegistrationStatus(selectedReg.registration_id, 'incomplete')}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    disabled={updating}
                    data-testid="incomplete-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Incomplete
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => updateRegistrationStatus(selectedReg.registration_id, 'waitlisted')}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white"
                    disabled={updating}
                  >
                    Waitlist
                  </Button>
                  {selectedReg.status !== 'archived' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => archiveRegistration(selectedReg.registration_id)}
                      disabled={updating}
                      data-testid="archive-btn"
                    >
                      <Archive className="w-4 h-4 mr-1" />
                      Archive
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => unarchiveRegistration(selectedReg.registration_id)}
                      disabled={updating}
                    >
                      Unarchive
                    </Button>
                  )}
                </div>
                
                {/* Delete Player - Separate row for visibility */}
                <div className="mt-4 pt-3 border-t border-red-200">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => openDeleteConfirm(selectedReg.player, selectedReg)}
                    disabled={updating}
                    data-testid="admin-delete-player-btn"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Player Permanently
                  </Button>
                  <span className="ml-2 text-xs text-gray-500">Remove player and all data</span>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setSelectedReg(null)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Review Modal */}
      {quickReviewPlayer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setQuickReviewPlayer(null); setQuickReviewData(null); }}>
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-navy flex items-center">
                <User className="w-5 h-5 mr-2" />
                Player Quick Review
              </h2>
              <div className="flex items-center gap-3">
                {quickReviewPlayer && (() => {
                  // Find the registration for this player to open Review on
                  const reg = registrations.find(r => r.player?.player_id === quickReviewPlayer);
                  if (!reg) return null;
                  return (
                    <button
                      type="button"
                      className="text-sm text-navy hover:underline font-medium whitespace-nowrap"
                      onClick={() => {
                        setQuickReviewPlayer(null);
                        setQuickReviewData(null);
                        openReviewModal(reg);
                      }}
                      data-testid="admin-quick-review-open-review-link"
                    >
                      Open Review →
                    </button>
                  );
                })()}
                <Button variant="ghost" size="sm" onClick={() => { setQuickReviewPlayer(null); setQuickReviewData(null); }}>
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {loadingQuickReview ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-navy border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : quickReviewData ? (
              <div className="p-4 space-y-4">
                {/* Player Info with Photo */}
                <div className="bg-navy/5 rounded-lg p-4">
                  <div className="flex items-center space-x-4">
                    <PlayerAvatar
                      photoUrl={quickReviewData.player?.photo_url}
                      firstName={quickReviewData.player?.first_name}
                      lastName={quickReviewData.player?.last_name}
                      size="xl"
                    />
                    <div className="min-w-0">
                      <h3 className="font-bold text-navy text-lg truncate">
                        {quickReviewData.player?.first_name || '—'} {quickReviewData.player?.last_name || ''}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                        {getRegDivisionLogo(quickReviewData) && (
                          <img 
                            src={getRegDivisionLogo(quickReviewData)} 
                            alt="" 
                            className="w-8 h-8 object-contain"
                          />
                        )}
                        <span>
                          {getDivisionDisplay(quickReviewData)}
                          {quickReviewData.player?.grade ? ` · Grade ${quickReviewData.player.grade}` : ''}
                        </span>
                      </div>
                      {quickReviewData.program_type && (
                        <div className="mt-1.5" data-testid="admin-quick-review-program">
                          {getProgramBadge(quickReviewData.program_type)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-sm border-t pt-3">
                    <div data-testid="admin-quick-review-dob"><span className="text-gray-500">DOB:</span> {parseLocalDate(quickReviewData.player?.date_of_birth)?.toLocaleDateString() ?? '—'}</div>
                    <div data-testid="admin-quick-review-position"><span className="text-gray-500">Position:</span> {quickReviewData.player?.position || '—'}</div>
                    <div><span className="text-gray-500">School:</span> {quickReviewData.player?.school || '—'}</div>
                    <div><span className="text-gray-500">Jersey:</span> #{quickReviewData.player?.jersey_number || '—'} ({quickReviewData.player?.jersey_size || '—'})</div>
                  </div>
                </div>

                {/* Registration Status */}
                <div className="border rounded-lg p-3">
                  <h4 className="font-semibold text-navy mb-2">Registration</h4>
                  <div className="flex items-center space-x-2">
                    <Badge className={getStatusBadge(quickReviewData.registration?.status).className}>
                      {getStatusBadge(quickReviewData.registration?.status).label}
                    </Badge>
                    <Badge className={getPaymentBadge(quickReviewData.registration?.payment_status).className}>
                      {getPaymentBadge(quickReviewData.registration?.payment_status).label}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Docs: {quickReviewData.document_count} | Waivers: {quickReviewData.waiver_count}
                  </p>
                </div>

                {/* Eligibility */}
                <div className="border rounded-lg p-3">
                  <h4 className="font-semibold text-navy mb-2">Eligibility</h4>
                  <Badge className={
                    quickReviewData.player?.eligibility_status === 'eligible' 
                      ? 'bg-green-100 text-green-800'
                      : quickReviewData.player?.eligibility_status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }>
                    {quickReviewData.player?.eligibility_status || 'Unknown'}
                  </Badge>
                </div>

                {/* Team / Division / Program */}
                <div className="border rounded-lg p-3" data-testid="admin-quick-review-team-block">
                  <h4 className="font-semibold text-navy mb-2">Team Assignment</h4>
                  {(() => {
                    const team = quickReviewData.team;
                    const divName = team?.division_name || quickReviewData.division_name || (quickReviewData.division_id ? DIVISION_DISPLAY[quickReviewData.division_id] : null);
                    const program = quickReviewData.program_type;
                    const programLabel = PROGRAM_INFO[program]?.name || program;
                    const hasAny = team?.name || divName || programLabel;
                    if (!hasAny) {
                      return <p className="text-sm text-gray-500">Not assigned</p>;
                    }
                    return (
                      <div className="text-sm space-y-0.5">
                        <div><span className="text-gray-500">Team:</span> {team?.name || '—'}</div>
                        <div><span className="text-gray-500">Division:</span> {divName || '—'}</div>
                        <div><span className="text-gray-500">Program:</span> {programLabel || '—'}</div>
                      </div>
                    );
                  })()}
                </div>

                {/* Medical Notes / Allergies */}
                <div className={`border rounded-lg p-3 ${quickReviewData.player?.medical_notes ? 'bg-amber-50 border-amber-200' : ''}`} data-testid="admin-quick-review-medical-notes">
                  <h4 className="font-semibold text-navy mb-2 flex items-center">
                    <HeartPulse className="w-4 h-4 mr-1 text-amber-700" />
                    Medical Notes / Allergies
                  </h4>
                  {quickReviewData.player?.medical_notes ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{quickReviewData.player.medical_notes}</p>
                  ) : (
                    <p className="text-sm text-gray-500">None on file</p>
                  )}
                </div>

                {/* Parent Contact (Primary) */}
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-navy">Parent / Guardian</h4>
                    {quickReviewData.household?.household_id && (
                      <button
                        onClick={() => setLinkGuardianCtx({
                          householdId: quickReviewData.household.household_id,
                          playerId: quickReviewData.player?.player_id,
                          playerName: `${quickReviewData.player?.first_name || ''} ${quickReviewData.player?.last_name || ''}`.trim(),
                        })}
                        className="text-xs font-semibold text-navy hover:underline"
                        data-testid="admin-quick-review-add-guardian-btn"
                      >
                        + {quickReviewData.parent ? 'Add Parent / Guardian' : 'Link Parent / Guardian'}
                      </button>
                    )}
                  </div>
                  {quickReviewData.parent ? (
                    <div className="flex items-start space-x-3">
                      <UserAvatar
                        photoUrl={quickReviewData.parent?.user?.picture}
                        name={quickReviewData.parent?.user?.name}
                        size="sm"
                      />
                      <div className="text-sm min-w-0">
                        {quickReviewData.parent?.user?.email ? (
                          <Link
                            to={`/admin/users?search=${encodeURIComponent(quickReviewData.parent.user.email)}`}
                            className="font-medium text-navy hover:underline"
                            data-testid="admin-quick-review-parent-link"
                          >
                            {quickReviewData.parent?.user?.name || '—'}
                          </Link>
                        ) : (
                          <span className="font-medium">{quickReviewData.parent?.user?.name || '—'}</span>
                        )}
                        <div className="text-gray-600">
                          {quickReviewData.parent?.user?.email ? (
                            <a className="hover:underline" href={`mailto:${quickReviewData.parent.user.email}`}>{quickReviewData.parent.user.email}</a>
                          ) : '—'}
                        </div>
                        <div className="text-gray-600">
                          {quickReviewData.parent?.user?.phone ? (
                            <a className="hover:underline" href={`tel:${quickReviewData.parent.user.phone}`}>{quickReviewData.parent.user.phone}</a>
                          ) : '—'}
                        </div>
                        {(() => {
                          const h = quickReviewData.household || {};
                          const addr = h.address;
                          let parts = [];
                          if (addr && typeof addr === 'object') {
                            parts = [addr.street, addr.city || h.city, addr.state || h.state, addr.zip || h.zip];
                          } else {
                            parts = [addr, h.city, h.state, h.zip];
                          }
                          const line = parts.filter(Boolean).join(', ');
                          if (!line) return null;
                          return (
                            <div className="text-gray-600 flex items-start mt-0.5">
                              <MapPin className="w-3.5 h-3.5 mr-1 mt-0.5 flex-shrink-0" />
                              <span>{line}</span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500" data-testid="admin-review-no-parent">No parent/guardian linked</p>
                  )}
                </div>

                {/* Secondary Parents/Guardians */}
                {Array.isArray(quickReviewData.secondary_parents) && quickReviewData.secondary_parents.length > 0 && (
                  <div className="border rounded-lg p-3" data-testid="admin-quick-review-secondary-parents">
                    <h4 className="font-semibold text-navy mb-2">Additional Guardian{quickReviewData.secondary_parents.length > 1 ? 's' : ''}</h4>
                    <div className="space-y-3">
                      {quickReviewData.secondary_parents.map((sp, idx) => (
                        <div key={sp.parent_id || idx} className="flex items-start space-x-3">
                          <UserAvatar photoUrl={sp.user?.picture} name={sp.user?.name} size="sm" />
                          <div className="text-sm min-w-0">
                            {sp.user?.email ? (
                              <Link to={`/admin/users?search=${encodeURIComponent(sp.user.email)}`} className="font-medium text-navy hover:underline">
                                {sp.user?.name || '—'}
                              </Link>
                            ) : (
                              <span className="font-medium">{sp.user?.name || '—'}</span>
                            )}
                            <div className="text-gray-600">
                              {sp.user?.email ? <a className="hover:underline" href={`mailto:${sp.user.email}`}>{sp.user.email}</a> : '—'}
                            </div>
                            <div className="text-gray-600">
                              {sp.user?.phone ? <a className="hover:underline" href={`tel:${sp.user.phone}`}>{sp.user.phone}</a> : '—'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Editable Status */}
                <div className="text-sm text-center text-gray-500 border-t pt-3">
                  {quickReviewData.is_editable 
                    ? "Parent can still edit this player's details"
                    : "This registration is locked (verified)"
                  }
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">Failed to load player data</div>
            )}
          </div>
        </div>
      )}

      {/* Admin Delete Confirmation Modal */}
      {showDeleteConfirm && playerToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center">
              <Trash2 className="w-5 h-5 mr-2" />
              Permanently Delete Player
            </h3>
            <p className="text-gray-700 mb-4">
              You are about to <span className="font-bold text-red-600">permanently delete</span>{' '}
              <span className="font-bold">{playerToDelete.first_name} {playerToDelete.last_name}</span> and all associated data.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800 font-medium">This action will:</p>
              <ul className="text-sm text-red-700 list-disc list-inside mt-1">
                <li>Delete all player information</li>
                <li>Remove all registration records</li>
                <li>Delete all uploaded documents</li>
                <li>Remove medical information</li>
              </ul>
              <p className="text-sm text-red-800 font-bold mt-2">This cannot be undone!</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold text-red-600">DELETE {playerToDelete.first_name.toUpperCase()}</span> to confirm:
              </label>
              <Input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={`DELETE ${playerToDelete.first_name.toUpperCase()}`}
                className="border-red-300 focus:border-red-500"
                data-testid="admin-delete-confirm-input"
              />
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleDeletePlayer}
                disabled={updating || deleteConfirmText.toUpperCase() !== `DELETE ${playerToDelete.first_name}`.toUpperCase()}
                data-testid="admin-delete-confirm-btn"
              >
                {updating ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Payment modal — finance-only. Backend also enforces RBAC. */}
      {isFinanceRole && (
        <ManualPaymentModal
          open={mpOpen}
          onClose={() => setMpOpen(false)}
          registration={selectedReg}
          onRecorded={async (result) => {
            // Refresh ledger + list. Also patch selectedReg locally so
            // the Payment Information card at the top of the modal
            // reflects the freshly recomputed amounts without waiting
            // for a re-open. Falls back gracefully if the response is
            // an OVERPAYMENT warning (no result.registration).
            await fetchLedger(selectedReg?.registration_id);
            if (result?.registration && selectedReg) {
              setSelectedReg((prev) => (prev ? {
                ...prev,
                total_amount: result.registration.total_amount ?? prev.total_amount,
                amount_paid: result.registration.amount_paid ?? prev.amount_paid,
                payment_status: result.registration.payment_status ?? prev.payment_status,
                standard_amount: result.registration.standard_amount ?? prev.standard_amount,
              } : prev));
            }
            await fetchRegistrations();
          }}
        />
      )}

      {linkGuardianCtx && (
        <AdminLinkGuardianModal
          householdId={linkGuardianCtx.householdId}
          playerId={linkGuardianCtx.playerId}
          playerName={linkGuardianCtx.playerName}
          onClose={() => setLinkGuardianCtx(null)}
          onSuccess={async () => {
            // Refresh both the enriched review data and the quick-review
            // payload so the Parent / Guardian card updates without a
            // full page reload.
            const pid = linkGuardianCtx?.playerId;
            if (pid) {
              try {
                const r = await axios.get(
                  `${API_URL}/api/admin/players/${pid}/quick-review`,
                  { withCredentials: true },
                );
                const qr = r.data || {};
                setQuickReviewData((prev) => (
                  prev && prev.player?.player_id === pid
                    ? { ...prev, parent: qr.parent || prev.parent, secondary_parents: qr.secondary_parents || [] }
                    : prev
                ));
                setSelectedReg((prev) => (
                  prev && prev.player?.player_id === pid
                    ? { ...prev, parent: qr.parent || prev.parent, secondary_parents: qr.secondary_parents || [] }
                    : prev
                ));
              } catch (_) { /* non-fatal */ }
            }
          }}
        />
      )}
    </ParentLayout>
  );
};

export default AdminRegistrationsList;
